
import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { Circle, CircleCheck } from "lucide-react"

import { cn } from "@/lib/utils"

interface StepProgressProps {
  steps: number;
  currentStep: number;
  className?: string;
}

export function StepProgress({ steps, currentStep, className }: StepProgressProps) {
  return (
    <div className={cn("relative w-full", className)}>
      <div className="absolute top-1/2 left-0 w-full h-1 -translate-y-1/2 bg-secondary rounded-full" />
      <div 
        className="absolute top-1/2 left-0 h-1 -translate-y-1/2 bg-fembot-pink rounded-full transition-all duration-300"
        style={{ width: `${Math.max(((currentStep - 1) / (steps - 1)) * 100, 0)}%` }}
      />
      <div className="relative flex justify-between">
        {Array.from({ length: steps }).map((_, index) => {
          const isCompleted = currentStep > index + 1;
          const isActive = currentStep === index + 1;
          
          return (
            <div key={index} className="flex flex-col items-center">
              <div className={cn(
                "h-8 w-8 rounded-full flex items-center justify-center transition-all",
                isCompleted ? "bg-fembot-pink text-white" : 
                isActive ? "bg-fembot-pink text-white" : 
                "bg-secondary text-gray-500 dark:text-gray-400"
              )}>
                {isCompleted ? (
                  <CircleCheck className="h-5 w-5" />
                ) : (
                  <Circle className={cn("h-5 w-5", isActive ? "text-white" : "")} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({ className, value, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
      className
    )}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className="h-full w-full flex-1 bg-primary transition-all"
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    />
  </ProgressPrimitive.Root>
))
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
